import React, { useState, useEffect } from 'react';
import { BASE_URL } from '../../apis/api';
import photoGalleryBg from '../../images/cover/photo-gallery-bg.jpg';
import VenoBox from 'venobox';
import { useNavigate } from 'react-router-dom';
import RouteConstants from '../../constant/Routeconstant';
import Loader from './Loader';

const PhotoGallery = () => {
    const [gallery, setGallery] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [showViewer, setShowViewer] = useState(false);

    const navigate = useNavigate();

    // Fetch gallery data
    useEffect(() => {
        const fetchGallery = async () => {
            try {
                const response = await fetch(`${BASE_URL}/photogallery/list-featuredphotogallery`);
                const result = await response.json();

                if (result.data && Array.isArray(result.data)) {
                    setGallery(result.data);
                } else {
                    setGallery([]);
                }
            } catch (err) {
                console.error('Gallery fetch error:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchGallery();
    }, []);

    // Initialize VenoBox when gallery changes
    useEffect(() => {
        if (gallery.length > 0) {
            new VenoBox({
                selector: '.photo-venobox',
                numeration: true,
                infinigall: true,
                share: true,
                spinner: 'rotating-plane',
                spinColor: '#F4D03F',
                titleattr: 'data-title',
                titlePosition: 'top',
                titleStyle: 'bar',
            });
        }
    }, [gallery]);

    const getImageUrl = (imagePath) => {
        return imagePath ? `${BASE_URL}/${imagePath}` : '/placeholder-image.jpg';
    };

    const handleViewAll = () => {
        navigate(RouteConstants.PHOTOGALERYLISTILFK);
    };

    const openViewer = (index) => {
        setCurrentIndex(index);
        setShowViewer(true);
    };

    const closeViewer = () => {
        setShowViewer(false);
    };

    const goToPrev = () => {
        setCurrentIndex((prevIndex) =>
            prevIndex === 0 ? gallery.length - 1 : prevIndex - 1
        );
    };

    const goToNext = () => {
        setCurrentIndex((prevIndex) =>
            prevIndex === gallery.length - 1 ? 0 : prevIndex + 1
        );
    };

    if (loading) {
        return (
            <Loader />
        );
    }

    if (!gallery.length) {
        return (
            <div className="py-16 text-center text-gray-500 bg-gray-50">
                No images found
            </div>
        );
    }

    return (
        <div
            className="py-12 px-4 bg-gray-50 photos"
            style={{
                backgroundImage: `url(${photoGalleryBg})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
            }}
        >
            <div className="max-w-7xl mx-auto">
                {/* Header with decorative elements */}
                <div className="text-center mb-12">
                    <div className="text-center mb-8 sm:mb-12 md:mb-16">
                        <div className="flex items-center justify-center mb-4 sm:mb-6">
                            <div className="h-px bg-gradient-to-r from-transparent via-yellow-400 to-transparent flex-1 max-w-16 sm:max-w-24 md:max-w-32"></div>
                            <h4 className="text-xl sm:text-2xl md:text-3xl font-semibold text-gray-800 mx-4 sm:mx-6 md:mx-8 relative">
                                <span className="bg-gradient-to-r from-gray-800 via-gray-700 to-gray-600 bg-clip-text text-white">
                                    FEATURED PHOTO GALLERY
                                </span>
                                <div className="absolute -bottom-2 sm:-bottom-2 left-1/2 transform -translate-x-1/2 w-10 sm:w-12 md:w-16 h-1 bg-gradient-to-r from-yellow-400 to-yellow-500 rounded-full"></div>
                            </h4>
                            <div className="h-px bg-gradient-to-r from-transparent via-yellow-400 to-transparent flex-1 max-w-16 sm:max-w-24 md:max-w-32"></div>
                        </div>
                    </div>
                </div>

                {/* Gallery List */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 px-4 cursor-pointer">
                    {gallery.map((item, index) => (
                        <a
                            key={item.id}
                            className="photo-venobox block bg-white rounded-lg overflow-hidden shadow-lg border-2 border-white transition-all duration-300 hover:shadow-xl"
                            data-href={getImageUrl(item?.photogallery?.image)}
                            data-gall="featured-gallery"
                            data-fitview="true"
                            data-title={item?.photogallery?.title}
                        >
                            <div className="aspect-[4/3] w-full relative">
                                <img
                                    src={getImageUrl(item?.photogallery?.image)}
                                    // alt={item?.photogallery?.title}
                                    alt='ilfk-gallery'
                                    className="w-full h-full object-cover"
                                    loading="lazy"
                                />
                            </div>
                        </a>
                    ))}
                </div>

                {/* View More Button */}
                <div className="text-center mt-10">
                    <button
                        onClick={handleViewAll}
                        className="group relative inline-flex items-center px-6 py-3 bg-[#efc847] text-black font-normal rounded-full 
                        hover:bg-gradient-to-r hover:from-yellow-500 hover:to-yellow-400 hover:text-gray-900 
                        transition-all duration-500 transform hover:scale-105 hover:shadow-xl 
                        border-2 border-transparent hover:border-yellow-300"
                    >
                        <span className="relative z-10">More Photos</span>
                    </button>
                </div>
            </div>

            {/* Custom Viewer Modal (alternative to VenoBox) */}
            {showViewer && (
                <div className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4">
                    <button
                        onClick={closeViewer}
                        className="absolute top-4 right-4 text-white text-2xl z-50"
                    >
                        &times;
                    </button>

                    <div className="relative w-full max-w-4xl h-full max-h-screen">
                        <img
                            src={getImageUrl(gallery[currentIndex]?.photogallery?.image)}
                            alt={gallery[currentIndex]?.photogallery?.title}
                            className="w-full h-full object-contain"
                        />

                        <div className="absolute bottom-4 left-0 right-0 text-center text-white">
                            {gallery[currentIndex]?.photogallery?.title}
                        </div>

                        <button
                            onClick={goToPrev}
                            className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 rounded-full"
                        >
                            &larr;
                        </button>

                        <button
                            onClick={goToNext}
                            className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 rounded-full"
                        >
                            &rarr;
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PhotoGallery;